class HeaderWrapper extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector('.shopify-section-header');
    this.exitTimer = null;
    this.onEnterHandler = this.onEnter.bind(this);
    this.onLeaveHandler = this.onLeave.bind(this);
    this.addEventListener('mouseenter', this.onEnterHandler);
    this.addEventListener('mouseleave', this.onLeaveHandler);
  }

  disconnectedCallback() {
    this.removeEventListener('mouseenter', this.onEnterHandler);
    this.removeEventListener('mouseleave', this.onLeaveHandler);
  }

  onEnter() {
    this.preventClose = true;
    this.header.classList.add('is-active');
    clearTimeout(this.exitTimer);
  }

  onLeave() {
    this.exitTimer = setTimeout(() => this.header.classList.remove('is-active'), 400);
  }
}

customElements.define('header-wrapper', HeaderWrapper)

customElements.define('sticky-header',
  class StickyHeader extends HeaderWrapper {
    constructor() {
      super();
      this.header = document.querySelector('.section-header');
      this.stickyType = this.getAttribute('data-sticky-type');
      this.headerBounds = {};
      this.currentScrollTop = 0;
      this.preventReveal = false;
      this.isScrolling = null;
      this.onScrollHandler = this.onScroll.bind(this);
      this.hideHeaderOnScrollUp = () => this.preventReveal = true;
      this.onResize = debounce(this.setHeaderHeight.bind(this), 100);
    }

    connectedCallback() {
      this.setHeaderHeight();
      window.addEventListener('resize', this.onResize);
      this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.addEventListener('scroll', this.onScrollHandler, false);
      this.createObserver();
    }

    disconnectedCallback() {
      this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.removeEventListener('scroll', this.onScrollHandler);
    }

    createObserver() {
      const observer = new IntersectionObserver(([entry]) => {
        this.headerBounds = entry.intersectionRect;
        if (this.headerBounds.top === 0 && this.headerBounds.bottom === 0) {
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const { top, bottom } = entry.boundingClientRect;
          this.headerBounds = { top: scrollTop + top, bottom: scrollTop + bottom };
        }
        observer.disconnect();
      });

      observer.observe(this.header);
    }

    setHeaderHeight() {
      requestAnimationFrame(() => {
        document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
      });
    }

    onScroll() {
      if (!this.headerBounds.bottom) return;
      const detailsEl = document.querySelector('details-modal.header__search details');
      const isOpen = detailsEl?.hasAttribute('open') || null; 
      if (isOpen) { this.reveal(); this.currentScrollTop = (window.pageYOffset || document.documentElement.scrollTop); return; }
      requestAnimationFrame(() => {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrolledPast = scrollTop > this.headerBounds.bottom;
        if (this.stickyType === 'on-scroll-up') {
          if (scrollTop > this.currentScrollTop && scrolledPast) {
            this.header.classList.add('scrolled-past-header');
            if (!this.preventHide) this.hide();
          } else if (scrollTop < this.currentScrollTop && scrolledPast) {
            this.header.classList.add('scrolled-past-header');
            if (!this.preventReveal) {
              this.reveal();
            } else {
              clearTimeout(this.isScrolling);
              this.isScrolling = setTimeout(() => (this.preventReveal = false), 100);
              this.hide();
            }
          } else if (scrollTop <= this.headerBounds.top) {
            this.header.classList.remove('scrolled-past-header');
            this.reset();
          }
        } else {
          if (scrolledPast) {
            this.header.classList.add('scrolled-past-header');
            if (!this.sticky()) {
              this.hide();
              setTimeout(() => this.reveal(), 100);
            }
          } else if (scrollTop <= this.headerBounds.top) {
            this.header.classList.remove('scrolled-past-header');
            this.reset();
          }
        }

        this.currentScrollTop = scrollTop;
      });
    }

    hide() {
      this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
      document.body.classList.add('shopify-body-section-header-hidden', 'shopify-body-section-header-sticky');
      this.closeMenuItems();
      this.closeSearchModal();
      this.closeHeaderDrawer();
    }

    reveal() {
      this.header.classList.add('shopify-section-header-sticky', 'animate');
      this.header.classList.remove('shopify-section-header-hidden');
      document.body.classList.add('shopify-body-section-header-sticky');
      document.body.classList.remove('shopify-body-section-header-hidden');
    }

    reset() {
      this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
    }

    sticky() {
      return this.header.classList.contains('shopify-section-header-sticky') && !this.header.classList.contains('shopify-section-header-hidden') && document.body.classList.contains('shopify-body-section-header-sticky') && !document.body.classList.contains('shopify-body-section-header-hidden');
    }

    closeHeaderDrawer() {
      this.headerDrawer = this.headerDrawer || this.header.querySelectorAll('header-drawer');
      this.headerDrawer.forEach((drawer) => drawer.isOpen && drawer.close());
    }

    closeMenuItems() {
      this.menuItems = this.menuItems || this.header.querySelectorAll('header-menu, header-submenu');
      this.menuItems.forEach((menuItem) => menuItem.closeDropdown());
    }

    closeSearchModal() {
      this.searchModals = this.searchModals || this.header.querySelectorAll('predictive-search');
      this.searchModals.forEach((searchModal) => searchModal.isOpen && searchModal.close(false));
    }
  }
)

customElements.define('account-icon',
  class AccountIcon extends HTMLSpanElement {
    constructor() {
      super();
      this.icon = this.querySelector('.icon');
    }

    connectedCallback() {
      document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
    }

    handleStorefrontSignInCompleted(event) {
      if (event?.detail?.avatar) this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
  , { extends: 'span' })

customElements.define('header-menu',
  class HeaderMenu extends HTMLElement {
    constructor() {
      super();
      this.header = document.querySelector('.header-wrapper');
      this.setPseudoclass();
      this.addEventListener('mouseenter', this.setMegaMenuOpen.bind(this));
      this.addEventListener('mouseleave', this.setMegaMenuClose.bind(this));
      this.addEventListener('focusin', this.openDropdown.bind(this));
      this.addEventListener('focusout', this.closeDropdown.bind(this));
    }

    setMegaMenuOpen() {
      const megaMenu = this.querySelector('mega-menu');
      megaMenu?.setAttribute('open', true);
    }

    setMegaMenuClose() {
      const megaMenu = this.querySelector('mega-menu');
      megaMenu?.removeAttribute('open');
    }

    openDropdown() {
      this.classList.add('active');
    }

    closeDropdown() {
      this.classList.remove('active');
    }

    setPseudoclass() {
      if (!this.header) return;
      const firstChild = this.header.firstElementChild;
      if (firstChild?.classList.contains('header--top-center') || firstChild?.classList.contains('header--top-left')) {
        this.style.setProperty('--p-header-menu', `var(--padding-bottom)`);
      } else {
        const padding = Math.floor((this.header.offsetHeight - this.offsetHeight) / 2);
        this.style.setProperty('--p-header-menu', `${padding}px`);
      }
    }
  }
)

class HeaderInlineSearch extends HTMLElement {
  connectedCallback() {
    this.toggleButton = this.querySelector('.header-inline-search__toggle');
    this.searchContent = this.querySelector('.header-inline-search__content');
    this.searchInput = this.searchContent?.querySelector('.search__input');

    if (!this.toggleButton || !this.searchContent || !this.searchInput) return;

    this.onToggle = this.toggleSearch.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);
    this.onDocumentClick = this.handleDocumentClick.bind(this);

    this.toggleButton.addEventListener('click', this.onToggle);
    this.addEventListener('keydown', this.onKeydown);
    document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
    this.toggleButton?.removeEventListener('click', this.onToggle);
    this.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onDocumentClick);
  }

  toggleSearch(event) {
    event?.preventDefault();

    if (this.classList.contains('is-open')) {
      this.closeSearch();
      return;
    }

    this.openSearch();
  }

  openSearch() {
    this.classList.add('is-open');
    this.toggleButton.setAttribute('aria-expanded', 'true');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.searchInput.focus({
          preventScroll: true
        });

        const position = this.searchInput.value.length;

        this.searchInput.setSelectionRange(position, position);
      });
    });
  }

  closeSearch() {
    this.classList.remove('is-open');
    this.toggleButton.setAttribute('aria-expanded', 'false');

    if (this.searchInput) {
      this.searchInput.setAttribute('aria-expanded', 'false');
      this.searchInput.blur();
    }

    if (this.predictiveSearch?.isOpen) {
      this.predictiveSearch.close(false);
    }
  }

  handleDocumentClick(event) {
    if (!this.classList.contains('is-open')) return;
    if (this.contains(event.target)) return;
    if (this.searchInput?.value.trim()) return;
    this.closeSearch();
  }

  handleKeydown(event) {
    if (
      event.key !== 'Escape' ||
      !this.classList.contains('is-open')
    ) {
      return;
    }

    this.closeSearch();
    this.toggleButton.focus();
  }
}

if (!customElements.get('header-inline-search')) {
  customElements.define('header-inline-search', HeaderInlineSearch);
}